import { useDebouncedFn } from '@utils/hooks';
import { useContext, useMemo } from 'react';
import {
  useCreateCampaignSettingsMutation,
  useGetCampaignSettingsQuery,
  useUpdateCampaignSettingsMutation,
} from '../subdomains/visitation/api';
import { enqueueSnackbar } from 'notistack';
import { CampaignContext } from '../stores/campaign-context';

export type UseAdSpend = {
  adSpend: number | null;
  updateSessionAdSpend?: (adSpend: number | null) => void;
  isFetching: boolean;
};

const SAVE_DELAY_MS = 1000;

const overallKey = '__overall__';

export type UseAdSpendOptions = {
  campaignId: number;
  overallAdSpend: number | null;
  partner: string | null;
};

export const useAdSpend = (options: UseAdSpendOptions): UseAdSpend => {
  const { campaignId, partner, overallAdSpend } = options;
  const { state: campaignState, dispatch } = useContext(CampaignContext);
  const partnerStateKey = partner ?? overallKey;
  const sessionAdSpend = campaignState.byPartner[partnerStateKey]?.adSpend;
  const { data: campaignSettingsResponse, isFetching: isFetchingSettings } =
    useGetCampaignSettingsQuery(campaignId);
  const [updateCampaignSettings] = useUpdateCampaignSettingsMutation();
  const [createCampaignSettings] = useCreateCampaignSettingsMutation();

  const campaignSetting = campaignSettingsResponse?.data.campaignSettings.find(
    setting => setting.partner === partner
  );
  const campaignSettingId = campaignSetting?.id;

  const changeCampaignSettings = useMemo(
    () =>
      async (
        params:
          | Parameters<typeof updateCampaignSettings>[0]
          | Parameters<typeof createCampaignSettings>[0]
      ) => {
        try {
          await (
            'id' in params
              ? updateCampaignSettings(params)
              : createCampaignSettings(params)
          ).unwrap();
        } catch (e) {
          console.error('Error saving ad spend:', e);
          enqueueSnackbar('Error saving ad spend', { variant: 'error' });
        }
      },
    [createCampaignSettings, updateCampaignSettings]
  );

  const debouncedUpdateCampaignSettings = useDebouncedFn(
    changeCampaignSettings,
    SAVE_DELAY_MS
  );

  const campaignSettingAdSpend = campaignSetting?.adSpend;

  const {
    adSpend,
    updateSessionAdSpend,
  }: Pick<UseAdSpend, 'adSpend' | 'updateSessionAdSpend'> = useMemo(() => {
    return {
      adSpend: sessionAdSpend ?? campaignSettingAdSpend ?? overallAdSpend ?? 0,
      updateSessionAdSpend: adSpend => {
        const resolvedAdSpend =
          typeof adSpend === 'number' ? adSpend : overallAdSpend;
        dispatch({
          type: 'ad_spend_changed',
          payload: { partner: partnerStateKey, adSpend: resolvedAdSpend },
        });
        (() =>
          void debouncedUpdateCampaignSettings(
            campaignSettingId
              ? {
                  adSpend: resolvedAdSpend ?? 0,
                  campaignId,
                  id: campaignSettingId,
                }
              : {
                  adSpend: resolvedAdSpend ?? 0,
                  campaignId,
                  partner,
                }
          ))();
      },
    };
  }, [
    sessionAdSpend,
    debouncedUpdateCampaignSettings,
    overallAdSpend,
    campaignSettingAdSpend,
    campaignId,
    partnerStateKey,
    campaignSettingId,
    dispatch,
    partner,
  ]);

  return useMemo(
    () => ({
      adSpend,
      updateSessionAdSpend,
      isFetching: isFetchingSettings,
    }),
    [adSpend, updateSessionAdSpend, isFetchingSettings]
  );
};
